import {
  ElementRef,
  Directive,
  AfterViewInit,
  OnDestroy,
  Input,
  Renderer2,
  RendererStyleFlags2,
  NgZone,
  Self,
  TemplateRef,
} from '@angular/core';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

@Directive({
  selector: '[baseTruncate]',
  hostDirectives: [
    {
      directive: NgbTooltip,
      inputs: ['ngbTooltip', 'placement', 'triggers', 'container', 'disableTooltip'],
    },
  ],
})
export class BaseTextTruncateDirective implements AfterViewInit, OnDestroy {
  // Custom tooltip content managed by `baseTruncate`. Do not use `[ngbTooltip]` on the host; use `[customTooltip]` instead.
  @Input() customTooltip: string | TemplateRef<any> = '';
  @Input() hideTooltip = false;
  // Lines to show before the ellipsis. 1 = classic single-line `text-truncate`; more = line clamp with wrapping.
  @Input() maxLines = 1;

  private resizeObserver?: ResizeObserver;
  /** The tooltip mirrors the host text, so it has to follow bound text that changes after init (e.g. a re-fetched row). */
  private mutationObserver?: MutationObserver;

  constructor(
    private elRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private ngZone: NgZone,
    @Self() private tooltip: NgbTooltip
  ) {}

  ngAfterViewInit(): void {
    if (this.maxLines > 1) {
      this.renderer.addClass(this.el, 'text-clamp');
      this.renderer.setStyle(this.el, '--text-clamp-lines', String(this.maxLines), RendererStyleFlags2.DashCase);
    } else {
      this.renderer.addClass(this.el, 'text-truncate');
    }
    this.recalculate();
    this.ngZone.runOutsideAngular(() => {
      this.mutationObserver = new MutationObserver(() => this.recalculate());
      this.mutationObserver.observe(this.el, { characterData: true, childList: true, subtree: true });
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.recalculate());
        this.resizeObserver.observe(this.el);
      } else {
        window.addEventListener('resize', this.onResize, { passive: true });
      }
    });
  }

  ngOnDestroy(): void {
    this.mutationObserver?.disconnect();
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.onResize);
    this.tooltip.close();
  }

  private onResize = () => this.recalculate();

  private get el(): HTMLElement {
    return this.elRef.nativeElement;
  }

  private recalculate(): void {
    requestAnimationFrame(() => {
      const el = this.el;
      const childTexts = Array.from(el.children)
        .map((c) => c.textContent?.trim())
        .filter((t) => t && t.length > 0);
      const isMultilineTooltip = childTexts.length > 1;

      const fullText = this.customTooltip
        ? this.customTooltip
        : isMultilineTooltip
        ? childTexts.join('\n')
        : (el.textContent || '').trim();

      const isOverflowing = el.scrollWidth > el.clientWidth || (this.maxLines > 1 && el.scrollHeight > el.clientHeight);

      if (this.hideTooltip || !fullText || (!isOverflowing && !this.customTooltip)) {
        this.ngZone.run(() => {
          this.tooltip.disableTooltip = true;
          this.tooltip.close();
        });
        return;
      }

      this.ngZone.run(() => {
        this.tooltip.disableTooltip = false;
        this.tooltip.container = 'body';
        this.tooltip.placement = this.tooltip.placement || 'top';
        this.tooltip.triggers = this.tooltip.triggers || 'hover';
        if (isMultilineTooltip) {
          this.tooltip.tooltipClass = 'multiline-tooltip';
        }
        this.tooltip.ngbTooltip = fullText;
      });
    });
  }
}
